import { useEffect, useMemo, useState } from 'react';
import { X, Save } from 'lucide-react';

import { DiffField } from './DiffField';
import { OverwriteGuardModal } from './OverwriteGuardModal';
import { ButtonTemplate } from '../../../components/ui/buttons/ButtonTemplate';
import { useGetDiffQuery } from '../api/parserImportApi';

// Enrich policy: which fields are ticked by default when opening the drawer
// for a "changed" record. Non-negotiable per spec §4.5.
const DEFAULT_TICKED = new Set(['price', 'features', 'agency_phone']);
const DEFAULT_UNTICKED = new Set(['description', 'title']);

// Fields that must never be applied by Enrich. `photos` lives on DO Spaces
// after import — enriching would replace uploaded URLs with source URLs.
const NON_ENRICHABLE_FIELDS = new Set(['photos']);

const MODES = [
  { value: 'enrich', label: 'Enrich (merge selected fields)' },
  { value: 'overwrite', label: 'Overwrite (replace all fields)' },
  { value: 'skip', label: 'Skip (do nothing)' },
];

// Right-side slide-in drawer showing the per-field diff and letting the
// user pick a mode + which fields to apply in Enrich mode.
export const DiffDrawer = ({ record, decision, token, onClose, onSave }) => {
  const parserAdvertId = record.parser_advert_id;

  const { data, isLoading, error } = useGetDiffQuery({
    token,
    parserAdvertId,
  });

  const [mode, setMode] = useState(decision?.mode || 'enrich');
  const [ticked, setTicked] = useState(() => new Set(decision?.selected_fields || []));
  const [showGuard, setShowGuard] = useState(false);

  // Initialize ticked defaults from spec once the diff loads.
  useEffect(() => {
    if (!data?.diffs) return;
    if (decision?.selected_fields?.length) {
      setTicked(
        new Set(decision.selected_fields.filter((f) => !NON_ENRICHABLE_FIELDS.has(f)))
      );
      return;
    }
    const next = new Set();
    data.diffs.forEach((d) => {
      if (!d.changed) return;
      if (NON_ENRICHABLE_FIELDS.has(d.field)) return;
      if (DEFAULT_UNTICKED.has(d.field)) return;
      // Ticked by default: explicit defaults OR anything else (safe merge).
      if (DEFAULT_TICKED.has(d.field) || !DEFAULT_UNTICKED.has(d.field)) {
        next.add(d.field);
      }
    });
    setTicked(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const manualEditFields = data?.manual_edits_summary || [];

  const handleModeChange = (m) => {
    if (m === 'overwrite' && manualEditFields.length > 0) {
      setShowGuard(true);
      return;
    }
    setMode(m);
  };

  const toggleField = (field, checked) => {
    setTicked((prev) => {
      const next = new Set(prev);
      if (checked) next.add(field);
      else next.delete(field);
      return next;
    });
  };

  const handleSave = () => {
    if (mode === 'skip') {
      onSave({ mode: 'skip' });
      return;
    }
    if (mode === 'overwrite') {
      onSave({ mode: 'overwrite', overwrite_confirmed: true });
      return;
    }
    const selected = [...ticked].filter((f) => !NON_ENRICHABLE_FIELDS.has(f));
    onSave({ mode: 'enrich', selected_fields: selected });
  };

  const visibleDiffs = useMemo(
    () => data?.diffs?.filter((d) => !NON_ENRICHABLE_FIELDS.has(d.field)) ?? [],
    [data]
  );

  const changedFieldsCount = useMemo(
    () => visibleDiffs.filter((d) => d.changed).length,
    [visibleDiffs]
  );

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
        aria-hidden
      />

      {/* Drawer */}
      <aside
        data-testid="diff-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Diff drawer"
        className="fixed top-0 right-0 z-50 h-screen w-full sm:w-[560px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right"
      >
        <div className="flex items-start justify-between p-4 border-b border-gray-200">
          <div className="flex flex-col gap-1 pr-6">
            <span className="text-xs text-darkGrayColor uppercase tracking-wider">
              Review changes
            </span>
            <h3 className="text-base font-semibold text-blackColor line-clamp-2">
              {record.title}
            </h3>
            <span className="text-xs text-darkGrayColor">
              {changedFieldsCount} changed field
              {changedFieldsCount !== 1 ? 's' : ''}
              {manualEditFields.length > 0 && (
                <>
                  {' · '}
                  <span
                    data-testid="diff-manual-edits-summary"
                    className="text-orangeColor"
                  >
                    {manualEditFields.length} manual edit
                    {manualEditFields.length > 1 ? 's' : ''}
                  </span>
                </>
              )}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-lightGrayColor"
            aria-label="Close diff drawer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Mode radio */}
        <div className="flex flex-col gap-1 p-4 border-b border-gray-200">
          <span className="text-xs text-darkGrayColor">Mode</span>
          <div className="flex flex-col gap-1">
            {MODES.map((m) => (
              <label
                key={m.value}
                className={
                  'flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer border transition-colors ' +
                  (mode === m.value
                    ? 'border-purpleColor bg-purpleColor/5'
                    : 'border-transparent hover:bg-lightGrayColor')
                }
              >
                <input
                  data-testid={`diff-mode-${m.value}`}
                  type="radio"
                  name="diff-mode"
                  value={m.value}
                  checked={mode === m.value}
                  onChange={() => handleModeChange(m.value)}
                  className="accent-purpleColor"
                />
                <span className="text-sm text-blackColor">{m.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Diff body */}
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
          {isLoading && (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-20 rounded border border-gray-200 bg-lightGrayColor animate-pulse"
                />
              ))}
            </div>
          )}
          {!isLoading && error && (
            <div className="text-sm text-redColor">Failed to load diff.</div>
          )}
          {!isLoading && !error && data?.diffs && (
            <>
              {visibleDiffs.map((diff) => (
                <DiffField
                  key={diff.field}
                  diff={diff}
                  currentValue={data.current?.[diff.field]}
                  newValue={data.new?.[diff.field]}
                  isTicked={ticked.has(diff.field)}
                  disabled={mode !== 'enrich'}
                  onToggle={toggleField}
                />
              ))}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-gray-200 bg-lightestGrayColor">
          <span className="text-xs text-darkGrayColor">
            {mode === 'enrich' && `${ticked.size} field${ticked.size !== 1 ? 's' : ''} will be applied`}
            {mode === 'overwrite' && 'All parser fields will replace backend values'}
            {mode === 'skip' && 'No changes will be applied'}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-darkGrayColor rounded border border-gray-200 hover:bg-lightGrayColor"
            >
              Cancel
            </button>
            <ButtonTemplate
              text="Save"
              icon={Save}
              isIconText={true}
              bgColor="bg-purpleColor border-purpleColor"
              handleClick={handleSave}
            />
          </div>
        </div>
      </aside>

      <OverwriteGuardModal
        open={showGuard}
        manualEditFields={manualEditFields}
        onCancel={() => setShowGuard(false)}
        onConfirm={() => {
          setShowGuard(false);
          setMode('overwrite');
        }}
      />
    </>
  );
};
